import { useTheme } from '@mui/material';
import dayjs, { Dayjs } from 'dayjs';
import { useMemo } from 'react';
import { FormAutocompleteProps, FormTextFieldProps } from 'src/components';
import { SAVE_DEBOUNCE_DELAY } from 'src/constants/delays';
import { componentsFontSizes } from 'src/constants/layout';
import {
  Project,
  ProjectStages,
  useGroupRules,
  usePlatformUsersOptions,
  useProjectStages,
  UserGroupRules,
} from 'src/graphql';
import { useProjectLostReasons } from 'src/graphql/entities/project-lost-reason';
import { ConditionInput } from 'src/graphql/types';
import { ProjectDateTriggerField } from './project-date-trigger-field';
import { ProjectLostReasonSelectMenuField } from './project-lost-reason-select-menu-field';
import { ProjectManagerSelectMenuField } from './project-manager-select-menu-field';
import { ProjectStageSelectMenuField } from './project-stage-select-menu-field';

export interface ProjectCardFormValues {
  projectStage: { id: string; label: string };
  projectManager: { id: string; label: string };
  projectLostReason: { id: number; label: string };
  lostReasonNotes: string;
  day1Date: dayjs.Dayjs;
  warrantyExpiration: dayjs.Dayjs;
  estimatedSigning: dayjs.Dayjs;
  estimatedDay1: dayjs.Dayjs;
}

interface ProjectCardFormProps {
  partnerId: number;
  project: Project;
}

export function formatProjectDate(date: Dayjs | null) {
  return date ? date.format('YYYY-MM-DD') : '';
}

const activeUserFilters: ConditionInput[] = [
  {
    attribute: 'isActive',
    operator: '=',
    value: 'true',
  },
];

export const useProjectCardForm = ({ partnerId, project }: ProjectCardFormProps) => {
  const theme = useTheme();
  const { projectStages, loading: loadingProjectStages } = useProjectStages();
  const { projectLostReasons, loading: loadingProjectLostReasons } = useProjectLostReasons();

  const { groupRules } = useGroupRules();

  const enabledChangeProjectStatus = Boolean(
    groupRules[UserGroupRules.AccessToChangeProjectStatus],
  );

  const userFilters = useMemo(() => {
    const filters = [...activeUserFilters];

    if (project.ownerId) {
      filters.push({
        attribute: 'userId',
        operator: '=',
        value: project.ownerId,
      });
    }

    return filters;
  }, [project.ownerId]);

  const { allUserRecords: currentUsers } = usePlatformUsersOptions(
    {
      partnerId,
      shouldIncludePlatformPartnerId: false,
      limit: 1,
      filters: userFilters,
    },
    {
      skip: !project.ownerId,
    },
  );

  const currentProjectManager = useMemo(() => currentUsers[0], [currentUsers]);

  const currentProjectStage = useMemo(
    () => projectStages.find((item) => item.id === project.projectStageId) || null,
    [project.projectStageId, projectStages],
  );

  const currentProjectLostReason = useMemo(() => {
    return projectLostReasons.find((item) => item.id === project.projectLostReasonId) || null;
  }, [project.projectLostReasonId, projectLostReasons]);

  const formFields = useMemo(() => {
    const commonProps = {
      autocompleteProps: {
        disabled: false,
        forcePopupIcon: false,
        disableClearable: true as const,
        sx: {
          padding: theme.spacing(0.5, 1),
          borderRadius: theme.shape.borderRadius / 8,
          cursor: 'pointer',
          '&:hover': {
            backgroundColor: theme.palette.custom.OV.sys.surface,
          },
          '& .MuiInput-root': {
            cursor: 'pointer',
            marginLeft: 0,
          },
          '& .MuiInput-input': {
            userSelect: 'none',
            cursor: 'pointer',
            fontSize: componentsFontSizes.input,
            '&::selection': {
              background: 'transparent',
            },
          },
          '&.Mui-focused': {
            backgroundColor: theme.palette.custom.OV.sys.surfaceContainerHigh,
          },
        },
        ListboxProps: {
          sx:
            theme.palette.mode === 'dark'
              ? {
                  background: theme.palette.custom.OV.sys.surfaceContainerHigh,
                  border: `1px solid ${theme.palette.custom.OV.sys.outlineVariant}`,
                  borderRadius: theme.shape.borderRadius / 8,
                }
              : {},
        },
      },
      textFieldProps: {
        size: 'small',
      },
      inputProps: {
        sx: {
          ...theme.typography.body2,
          fontSize: theme.utils.important(componentsFontSizes.input),
        },
      },
    };

    const shouldShowDay1Date = project.projectStageId === ProjectStages.ServiceClient;
    const shouldShowWarrantyExpiration =
      project.projectStageId === ProjectStages.ServiceClient;
    const shouldShowEstimatedSigning = project.projectStageId === ProjectStages.Proposal;
    const shouldShowEstimatedDay1 =
      project.projectStageId === ProjectStages.Proposal ||
      project.projectStageId === ProjectStages.ActiveProject;
    const shouldShowLostReason = project.projectStageId === ProjectStages.ProposalLost;

    return [
      {
        type: 'autocomplete',
        name: 'projectStage' as const,
        label: 'Stage',
        required: 'Stage is required',
        options: projectStages.map((stage) => ({
          id: String(stage.id),
          label: stage.name,
        })),
        defaultValue: currentProjectStage
          ? { id: String(currentProjectStage.id), label: currentProjectStage.label }
          : null,
        ...commonProps,
        renderField: (field: FormAutocompleteProps, formDisabled?: boolean) => (
          <ProjectStageSelectMenuField field={field} disabled={formDisabled} />
        ),
        autocompleteProps: {
          ...commonProps.autocompleteProps,
          disabled: commonProps.autocompleteProps.disabled || !enabledChangeProjectStatus,
          loading: loadingProjectStages,
        },
      },
      {
        type: 'autocomplete',
        name: 'projectManager' as const,
        label: 'Manager',
        required: 'Manager is required',
        options: [],
        defaultValue: currentProjectManager ?? null,
        ...commonProps,
        renderField: (field: FormAutocompleteProps, formDisabled?: boolean) => (
          <ProjectManagerSelectMenuField
            field={field}
            partnerId={partnerId}
            disabled={formDisabled}
          />
        ),
        textFieldProps: {
          ...commonProps.textFieldProps,
          placeholder: 'Assign',
        },
      },
      {
        type: 'textfield',
        name: 'day1Date' as const,
        defaultValue: project.day1StartDate ? dayjs(project.day1StartDate) : null,
        label: 'Day1 Date',
        renderField: (field: FormTextFieldProps, formDisabled?: boolean) => (
          <ProjectDateTriggerField field={field} disabled={formDisabled} />
        ),
        hidden: !shouldShowDay1Date,
      },
      {
        type: 'textfield',
        name: 'warrantyExpiration' as const,
        defaultValue: project.warrantyExpirationDate
          ? dayjs(project.warrantyExpirationDate)
          : null,
        label: 'Warranty Exp.',
        renderField: (field: FormTextFieldProps, formDisabled?: boolean) => (
          <ProjectDateTriggerField field={field} disabled={formDisabled} />
        ),
        hidden: !shouldShowWarrantyExpiration,
      },
      {
        type: 'textfield',
        name: 'estimatedSigning' as const,
        defaultValue: project.signingDate ? dayjs(project.signingDate) : null,
        label: 'Est. Signing',
        renderField: (field: FormTextFieldProps, formDisabled?: boolean) => (
          <ProjectDateTriggerField field={field} disabled={formDisabled} />
        ),
        hidden: !shouldShowEstimatedSigning,
      },
      {
        type: 'textfield',
        name: 'estimatedDay1' as const,
        defaultValue: project.estimatedDay1Date ? dayjs(project.estimatedDay1Date) : null,
        label: 'Est. Day1',
        renderField: (field: FormTextFieldProps, formDisabled?: boolean) => (
          <ProjectDateTriggerField field={field} disabled={formDisabled} />
        ),
        hidden: !shouldShowEstimatedDay1,
      },
      {
        type: 'autocomplete',
        name: 'projectLostReason' as const,
        label: 'Lost Reason',
        options: projectLostReasons.map((stage) => ({ id: stage.id, label: stage.name })),
        defaultValue: currentProjectLostReason,
        ...commonProps,
        renderField: (field: FormAutocompleteProps, formDisabled?: boolean) => (
          <ProjectLostReasonSelectMenuField field={field} disabled={formDisabled} />
        ),
        hidden: !shouldShowLostReason,
      },
      {
        type: 'rich-text-bubble',
        label: 'Notes',
        name: 'lostReasonNotes' as const,
        value: project?.lostReasonNotes || '',
        debounced: SAVE_DEBOUNCE_DELAY,
        sx: {
          padding: theme.spacing(1),
        },
        hidden: !shouldShowLostReason,
      },
    ] as FormAutocompleteProps[];
  }, [
    currentProjectLostReason,
    currentProjectManager,
    currentProjectStage,
    enabledChangeProjectStatus,
    loadingProjectStages,
    partnerId,
    project.day1StartDate,
    project.estimatedDay1Date,
    project?.lostReasonNotes,
    project.projectStageId,
    project.signingDate,
    project.warrantyExpirationDate,
    projectLostReasons,
    projectStages,
    theme,
  ]);

  return {
    formFields,
    loading: loadingProjectStages || loadingProjectLostReasons,
  };
};
